// assets/blog-reveal.js
(() => {
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const prefersReducedMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const hero = document.querySelector(".article-template__hero-container");
  const img = document.querySelector(".article-template__hero-container img");
  if (!hero || !img) return;

  // =========================
  // 1) Luxury reveal
  // =========================
  (() => {
    const elements = [
      hero,
      document.querySelector(".article-template__title"),
      document.querySelector(".article-template__content")
    ].filter(Boolean);

    if (!elements.length) return;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("reveal-in"));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("reveal-in");
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -5% 0px" }
    );

    elements.forEach((el) => io.observe(el));
  })();

  // =========================
  // 2) Ensure hero background layer exists (.hero-bg)
  // =========================
  let bg = hero.querySelector(".hero-bg");
  if (!bg) {
    bg = document.createElement("div");
    bg.className = "hero-bg";
    hero.insertBefore(bg, hero.firstChild);
  }

  const setBgFromImg = () => {
    const src = img.currentSrc || img.src;
    if (!src) return;
    bg.style.backgroundImage = `url("${src}")`;
  };

  if (img.complete) setBgFromImg();
  else img.addEventListener("load", setBgFromImg, { once: true });

  window.addEventListener("resize", () => requestAnimationFrame(setBgFromImg));

  // =========================
  // 3) Ensure overlay exists (.hero-overlay + .hero-grain)
  // =========================
  let overlay = hero.querySelector(".hero-overlay");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.className = "hero-overlay";

    const grain = document.createElement("div");
    grain.className = "hero-grain";

    overlay.appendChild(grain);
    hero.appendChild(overlay);
  }

  // =========================
  // 4) Wrap image for parallax/zoom (wrapper gets transform)
  // =========================
  let wrap = hero.querySelector(".hero-zoom-wrap");
  if (!wrap) {
    wrap = document.createElement("div");
    wrap.className = "hero-zoom-wrap";
    img.parentNode.insertBefore(wrap, img);
    wrap.appendChild(img);
  }

  // =========================
  // 5) Blur -> Sharp
  // =========================
  (() => {
    const makeSharp = () => img.classList.add("is-sharp");

    if (prefersReducedMotion) {
      makeSharp();
      return;
    }

    if (img.complete && img.naturalWidth > 0) {
      requestAnimationFrame(makeSharp);
      return;
    }

    img.addEventListener("load", () => requestAnimationFrame(makeSharp), { once: true });
    setTimeout(makeSharp, 1200);
  })();

  // =========================
  // 6) Detect aspect ratio => add class (square/portrait/landscape)
  // =========================
  const applyRatioMode = () => {
    if (!img.naturalWidth || !img.naturalHeight) return;

    const ratio = img.naturalWidth / img.naturalHeight;

    hero.classList.remove("is-square", "is-portrait", "is-landscape");

    if (ratio > 0.9 && ratio < 1.1) hero.classList.add("is-square");
    else if (ratio <= 0.9) hero.classList.add("is-portrait");
    else hero.classList.add("is-landscape");

    // Guarantee: no crop for square/portrait (zoom would crop because overflow hidden)
    if (hero.classList.contains("is-square") || hero.classList.contains("is-portrait")) {
      wrap.style.transform = "none";
    }
  };

  if (img.complete) applyRatioMode();
  else img.addEventListener("load", applyRatioMode, { once: true });

  // =========================
  // 7) Parallax-lite zoom (LANDSCAPE only)
  // =========================
  (() => {
    if (prefersReducedMotion) return;

    let ticking = false;

    const update = () => {
      ticking = false;

      // disable zoom for square/portrait
      if (!hero.classList.contains("is-landscape")) {
        wrap.style.transform = "none";
        return;
      }

      const rect = hero.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;

      const progress = clamp((vh - rect.top) / (vh + rect.height), 0, 1);

      const scale = 1 + progress * 0.08;
      const translateY = (progress - 0.5) * 12;

      wrap.style.transform = `scale(${scale.toFixed(4)}) translateY(${translateY.toFixed(2)}px)`;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    update();
  })();

  // =========================
  // 8) Dynamic light (vars on overlay)
  // =========================
  (() => {
    const target = overlay;
    const setVars = (x, y) => {
      target.style.setProperty("--lx", `${x}%`);
      target.style.setProperty("--ly", `${y}%`);
    };

    setVars(40, 28);

    if (prefersReducedMotion) return;

    const canHover =
      window.matchMedia && window.matchMedia("(hover: hover)").matches;

    let raf = null;

    const onMove = (e) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;

        const cx = Math.min(70, Math.max(30, x));
        const cy = Math.min(55, Math.max(15, y));
        setVars(cx, cy);
      });
    };

    if (canHover) {
      hero.addEventListener("mousemove", onMove);
      hero.addEventListener("mouseleave", () => setVars(40, 28));
    } else {
      let t = 0;
      const breathe = () => {
        t += 0.008;
        const x = 45 + Math.sin(t) * 6;
        const y = 28 + Math.cos(t) * 4;
        setVars(x, y);
        raf = requestAnimationFrame(breathe);
      };
      breathe();
    }
  })();
})();
